import { clsx } from 'clsx'
import { isArray } from 'lodash-es'
import { useSendMenuFeedback } from '@tovala/browser-apis-combinedapi'
import { MealBackgroundStyle } from '@tovala/component-library'

import { ErrorCodeMessageMapCombinedAPI } from 'types/internal'
import { storageAvailable } from 'utils/storageAvailable'
import { HEADING_CLASS_MAP } from './backgroundStyles'

import { useToast } from 'contexts/toast'
import { useUser } from 'contexts/user'
import APIErrorDisplay from 'components/common/APIErrorDisplay'

const SEND_FEEDBACK_ERRORS: ErrorCodeMessageMapCombinedAPI = {
  Fallback: {
    helpToFix: 'Please try again.',
    why: "We couldn't save your feedback due to a technical issue on our end.",
  },
}

const MenuFeedback = ({
  onMenuFeedbackSaved,
  termID,
  ...rest
}: {
  backgroundStyle?: MealBackgroundStyle
  onMenuFeedbackSaved(rating: number): void
  options: { label: string; value: number }[]
  subtitle?: string
  termID: number
  title: string
}) => {
  const { openToast } = useToast()

  const { user } = useUser()

  const {
    error: sendMenuFeedbackError,
    isError: hasSendMenuFeedbackError,
    mutate: sendMenuFeedback,
  } = useSendMenuFeedback({
    onSuccess: (...args) => {
      const { rating } = args[1].data.records[0].fields

      if (storageAvailable('localStorage')) {
        let menuFeedback = localStorage.getItem('menuFeedback')

        menuFeedback = menuFeedback ? JSON.parse(menuFeedback) : []

        if (isArray(menuFeedback)) {
          menuFeedback.push(termID)

          localStorage.setItem('menuFeedback', JSON.stringify(menuFeedback))
        }
      }

      onMenuFeedbackSaved(rating)

      openToast({
        heading: 'Feedback Saved',
        message: 'Thanks for your feedback!',
        type: 'success',
      })
    },
  })

  return (
    <MenuFeedbackDisplay
      {...rest}
      onClickRating={(rating) => {
        sendMenuFeedback({
          data: {
            records: [
              {
                fields: {
                  createdAt: new Date().toISOString(),
                  rating,
                  source: 'web' as const,
                  termID,
                  userID: user.id,
                },
              },
            ],
          },
        })
      }}
      saveError={hasSendMenuFeedbackError ? sendMenuFeedbackError : null}
    />
  )
}

export default MenuFeedback

export const MenuFeedbackDisplay = ({
  backgroundStyle = 'light',
  onClickRating,
  options,
  saveError,
  subtitle,
  title,
}: {
  backgroundStyle?: MealBackgroundStyle
  onClickRating(rating: number): void
  options: { label: string; value: number }[]
  saveError: Error | null
  subtitle?: string
  title: string
}) => {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <p
          className={clsx(
            'mb-3 text-k/20_125 transition-colors duration-300 md:text-k/16_125',
            HEADING_CLASS_MAP[backgroundStyle],
          )}
        >
          {title}
        </p>
        {subtitle && (
          <p
            className={clsx(
              'mb-6 text-k/16_125 transition-colors duration-300 md:text-k/14_120',
              backgroundStyle === 'dark' ? 'text-grey-1' : 'text-grey-8',
            )}
          >
            {subtitle}
          </p>
        )}
        <div className="cursor-pointer space-x-4 text-3xl">
          {options.map(({ label, value }, i) => {
            return (
              <span
                key={i}
                className="grayscale transition-all hover:grayscale-0"
                onClick={() => {
                  onClickRating(value)
                }}
              >
                {label}
              </span>
            )
          })}
        </div>
      </div>

      {saveError && (
        <div className="mx-auto max-w-menu md:max-w-full">
          <APIErrorDisplay
            error={saveError}
            errorCodeMessageMap={SEND_FEEDBACK_ERRORS}
          />
        </div>
      )}
    </div>
  )
}
